'use client';

import React from 'react';
import { cn } from '../lib/utils';
import { Button } from './button';
import {
  controlFrameClassName,
  controlSizeClassName,
  type ControlDensity,
  type ControlSize,
} from './control-styles';

export interface SegmentedControlProps<Value extends string> extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  options: readonly { value: Value; label: string; disabled?: boolean }[];
  value: Value;
  onChange: (value: Value) => void;
  'aria-label': string;
  label?: string;
  size?: ControlSize;
  density?: ControlDensity;
  className?: string;
}

/** One shared outer frame; segments fit inside it without adding another border. */
export function SegmentedControl<Value extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  label,
  size = 'toolbar',
  density,
  className,
  ...props
}: SegmentedControlProps<Value>) {
  return (
    <div
      {...props}
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        controlFrameClassName,
        controlSizeClassName(size, density),
        'inline-flex min-w-0 items-center border-border bg-surface-soft p-0',
        className,
      )}
    >
      {label && <span className="px-2 text-text-muted">{label}</span>}
      {options.map((option) => (
        <Button
          key={option.value}
          type="button"
          role="radio"
          variant="ghost"
          size={size}
          density={density}
          disabled={option.disabled}
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'h-full min-h-0 rounded-none border-0 py-0 first:rounded-l-control last:rounded-r-control',
            density === 'comfort' &&
              'first:rounded-l-control-comfort last:rounded-r-control-comfort',
            value === option.value
              ? 'bg-surface-sheet text-text-heading'
              : 'text-text-muted hover:text-text-heading',
          )}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}
